import './index.scss'

import { useAuth } from '@auth/AuthProvider'
import LoadingIndicator from '@components/LoadingIndicator'
import LaunchpadController from '@data/controller/LaunchpadController'
import {
  Launchpad,
  launchpadFromMetadata,
} from '@interfaces/launchpad.interface'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LaunchpadViewModal from '@pages/v2/Launchpad/Modal/LaunchpadViewModal'

import plusIcon from 'url:@assets/icons/plusNoCircle.svg';
import chevronRightIcon from 'url:@assets/icons/chevron.right.plain.svg';
import launchpadIcon from 'url:@assets/icons/rocket.svg';

export default function LaunchpadViewsPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [launchpads, setLaunchpads] = useState<Launchpad[]>([]);
  const [isLaunchpadModalOpen, setIsLaunchpadModalOpen] = useState(false);
  const [newLaunchpadModalKey, setNewLaunchpadModalKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadLaunchpads = async () => {
      if (!auth.churchId) return;

      setLoading(true);
      setError(null);

      const response = await LaunchpadController.fetchLaunchpads(auth.churchId, auth);

      if (isCancelled) {
        return;
      }

      if (!response.body) {
        setError(response.error || 'Failed to load Launchpads.');
        setLoading(false);
        return;
      }

      setLaunchpads(response.body.results?.map((metadata) => launchpadFromMetadata(metadata)));
      setLoading(false);
    }

    loadLaunchpads();

    return () => {
      isCancelled = true;
    }
  }, [auth.churchId, auth.consumeRefreshToken]);

  const openLaunchpad = (launchpad: Launchpad) => {
    navigate(`/launchpad/${launchpad.launchpadID}`, {
      state: {
        launchpad
      }
    });
  };

  if (loading) {
    return <div className='launchpad-views-page launchpad-state'>
      <LoadingIndicator />
    </div>
  }

  return (
    <div className='launchpad-views-page'>
      {error ? <p className='banner error'>{error}</p> : null}

      <LaunchpadViewModal
        key={`new-launchpad-${newLaunchpadModalKey}`}
        isOpen={isLaunchpadModalOpen}
        launchpad={null}
        close={() => setIsLaunchpadModalOpen(false)}
        onSaved={(savedLaunchpad) => {
          setIsLaunchpadModalOpen(false);
          openLaunchpad(savedLaunchpad);
        }}
      />

      <div className='launchpad-layout'>
        <div className='launchpad-list-column'>
          {launchpads.length === 0 ? <div className='empty-state card'>
            <h3>No Launchpad Views yet</h3>
            <p>A Launchpad View can be made for as many campuses or categories as you want.</p>
          </div> : null}

          <div className='launchpad-card-grid'>
            {launchpads.map((launchpad) => {
              return <LaunchpadCard
                key={launchpad.launchpadID}
                launchpad={launchpad}
                onClick={() => openLaunchpad(launchpad)}
              />
            })}

            <button type='button' className='new-launchpad-card' onClick={() => {
              setNewLaunchpadModalKey((currentKey) => currentKey + 1);
              setIsLaunchpadModalOpen(true);
            }}>
              <img src={plusIcon} alt='' aria-hidden='true' />
              New Launchpad View
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function LaunchpadCard(props: {
  launchpad: Launchpad;
  onClick: () => void;
}) {
  const attributes = props.launchpad.metadata.attributes;

  return <button type='button' className='launchpad-card card' onClick={props.onClick}>
    <div className='launchpad-card-logo'>
      {attributes.logoURL ?
        <img src={attributes.logoURL} alt={`${attributes.name} logo`} /> :
        <img src={launchpadIcon} alt='' aria-hidden='true' className='placeholder' />}
    </div>

    <div className='launchpad-card-heading'>
      <h3>{attributes.name}</h3>
      <img src={chevronRightIcon} alt='' aria-hidden='true' className='chevron' />
    </div>

    <p className='launchpad-card-description'>{attributes.description}</p>
  </button>
}
